import { useState, useEffect, useCallback, useRef } from 'react';
import type { GeofenceConfig, GeofenceEvent } from '@caro/shared/types';
import { API_ENDPOINTS } from '@caro/shared/constants';
import { useAuth } from './useAuth';
import { NativeBridge } from '../native/NativeBridge';

interface UseGeofenceReturn {
  config: GeofenceConfig | null;
  isTracking: boolean;
  isInsideFence: boolean | null;
  lastPosition: { lat: number; lng: number } | null;
  error: string | null;
  startTracking: () => void;
  stopTracking: () => void;
}

/**
 * Geofence monitoring hook.
 * Uses Geolocation API (browser) or @capacitor-community/background-geolocation (native).
 * Monitors resident position relative to configured geofence boundary.
 */
export function useGeofence(): UseGeofenceReturn {
  const { api } = useAuth();
  const [config, setConfig] = useState<GeofenceConfig | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [isInsideFence, setIsInsideFence] = useState<boolean | null>(null);
  const [lastPosition, setLastPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const watchIdRef = useRef<number | null>(null);

  // Fetch geofence config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const result = await api.get<{ success: boolean; config: GeofenceConfig }>(
          API_ENDPOINTS.USER_GEOFENCE_CONFIG,
        );
        if (result.config?.isActive) {
          setConfig(result.config);
        }
      } catch {
        // Geofence not configured — that's fine
      }
    };
    fetchConfig();
  }, [api]);

  const checkPosition = useCallback((lat: number, lng: number) => {
    if (!config) return;

    const distance = haversineDistance(
      lat, lng,
      config.centerLatitude, config.centerLongitude,
    );

    const inside = distance <= config.radiusMeters;
    setIsInsideFence(inside);
    setLastPosition({ lat, lng });

    // Send event to API if exiting geofence
    if (!inside) {
      const event: Omit<GeofenceEvent, 'userId' | 'geofenceId'> = {
        eventType: 'exit',
        latitude: lat,
        longitude: lng,
        timestamp: new Date().toISOString(),
      };
      api.post(API_ENDPOINTS.ALERTS_GEOFENCE, event).catch(() => {
        // Best effort — will retry on next position update
      });
    }
  }, [config, api]);

  const startTracking = useCallback(() => {
    if (!config || isTracking) return;

    if (!navigator.geolocation) {
      setError('Geolocation not available');
      return;
    }

    setIsTracking(true);
    setError(null);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        checkPosition(pos.coords.latitude, pos.coords.longitude);
      },
      (err) => {
        setError(err.message);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 30_000,
        timeout: 10_000,
      },
    );
  }, [config, isTracking, checkPosition]);

  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsTracking(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  return { config, isTracking, isInsideFence, lastPosition, error, startTracking, stopTracking };
}

/**
 * Haversine distance between two lat/lng points in meters.
 */
function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371e3; // Earth radius in meters
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}
